import { useCallback, useEffect, useState } from 'react'
import { clock, countdown, destinationOf, ferry, money, originOf, type Trip, type TripsView } from '../lib/ferry'
import { t } from '../lib/i18n'
import Icon from './Icon'
import TripPlan, { savedTravelMinutes } from './TripPlan'

const dayLabel = (ms: number) =>
  new Intl.DateTimeFormat(undefined, { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'America/Puerto_Rico' }).format(ms)

/**
 * My trips.
 *
 * A list of rows answers "what have I bought". The question somebody actually
 * opens this with is "am I about to miss something" — so the next trip is the
 * screen and everything else is a tail underneath it.
 */
export default function MyTrips({ onPay, onPass }: {
  onPay: (t: Trip) => void
  onPass: (bookingId: string) => void
}) {
  const [view, setView] = useState<TripsView | null>(null)
  const [error, setError] = useState('')
  const [openId, setOpenId] = useState<string | null>(null)

  const load = useCallback(() => {
    ferry.trips(savedTravelMinutes())
      .then((v) => {
        setView(v)
        setError('')
        /*
         * Read the CURRENT openId from the setter rather than the closure.
         *
         * `load` is also called after a cancel, and closing over `openId` meant
         * that later call still saw the value from first render — so a card the
         * user had opened could be silently reopened, or a newly-arrived next
         * trip refuse to open because a stale null said something was already
         * open. The functional form always sees the truth.
         */
        if (v.next) setOpenId((current) => current ?? v.next!.id)
      })
      .catch((e: Error) => setError(e.message))
  }, [])
  useEffect(() => { load() }, [load])

  if (error) return <section className="card"><p className="error">{error}</p></section>
  if (!view) return <section className="card"><p className="muted">{t('app.loading')}</p></section>

  if (view.trips.length === 0) {
    return (
      <section className="card empty">
        <Icon name="ferry" size={40} />
        <h1>{t('trips.noneTitle')}</h1>
        <p className="muted">{t('trips.noneBody')}</p>
      </section>
    )
  }

  return (
    <section className="card">
      <h1>{t('nav.trips')}</h1>
      <p className="muted">
        {view.remaining === 0 ? t('trips.noneUpcoming')
          : `${view.remaining} ${view.remaining === 1 ? t('trips.one') : t('trips.many')}`}
        {view.unpaid > 0 && <> · <b className="warn-text">{view.unpaid} {t('trips.unpaid')}</b></>}
      </p>

      {view.upcoming.map((trip) => (
        <TripCard
          key={trip.id} trip={trip}
          open={openId === trip.id}
          onToggle={() => setOpenId(openId === trip.id ? null : trip.id)}
          onPay={onPay} onPass={onPass} onChanged={load}
        />
      ))}

      {view.past.length > 0 && (
        <details className="gone">
          <summary>{view.past.length} {t('trips.past')}</summary>
          {view.past.map((trip) => (
            <div key={trip.id} className="trip-card past">
              <div className="trip-head">
                <span className="trip-when muted">{dayLabel(trip.scheduled_departure)} · {clock(trip.scheduled_departure)}</span>
                <span className="trip-route">{originOf(trip.route)} → {destinationOf(trip.route)}</span>
                <span className="muted small">{money(trip.total_cents / 100)}</span>
              </div>
            </div>
          ))}
        </details>
      )}
    </section>
  )
}

function TripCard({ trip, open, onToggle, onPay, onPass, onChanged }: {
  trip: Trip
  open: boolean
  onToggle: () => void
  onPay: (t: Trip) => void
  onPass: (id: string) => void
  onChanged: () => void
}) {
  const [busy, setBusy] = useState(false)

  /*
   * The card LIGHTS UP when it is time to move.
   *
   * `urgency` is decided on the server from the same plan arithmetic, so what
   * glows here and what the timeline says are the same judgement. Colour is the
   * fastest thing on a screen and it is spent only on `now` — if everything
   * glows, nothing does.
   */
  return (
    <div className={`trip-card urgency-${trip.urgency}${trip.status === 'held' ? ' unpaid' : ''}`}>
      <button className="trip-head" onClick={onToggle} aria-expanded={open}>
        <span className="trip-when">
          <span className="muted small">{dayLabel(trip.scheduled_departure)}</span>
          <strong>{clock(trip.scheduled_departure)}</strong>
          {trip.delayMinutes ? <em className="late">{trip.delayMinutes} {t('sail.late')}</em> : null}
        </span>
        <span className="trip-route">
          <strong>{originOf(trip.route)} <Icon name="chevron" size={13} /> {destinationOf(trip.route)}</strong>
          <em className="muted">
            {trip.party_size} {trip.party_size === 1 ? t('pass.traveller') : t('pass.travellers')}
            {trip.hasVehicle && <> · <Icon name="car" size={13} /></>}
          </em>
        </span>
        <span className="trip-state">
          {trip.status === 'held'
            ? <em className="warn-text">{t('status.held')}</em>
            : trip.urgency === 'now'
              ? <em className="live">{t('trips.timeToGo')}</em>
              : <em className="muted">{countdown(trip.minutesToDeparture)}</em>}
        </span>
        <Icon name="chevron" size={16} className={`col-caret${open ? ' open' : ''}`} />
      </button>

      {open && (
        <div className="trip-body">
          {trip.status === 'held' && (
            <div className="notice warn">
              <Icon name="clock" size={16} />
              <span>
                {t('status.heldWhy')}
                <button className="primary with-icon" onClick={() => onPay(trip)}>
                  <Icon name="shield" size={16} />{t('pay.finish')} · {money(trip.total_cents / 100)}
                </button>
              </span>
            </div>
          )}

          {/* The whole plan, ported from travel-day. */}
          <TripPlan trip={trip} />

          <div className="trip-actions">
            {trip.status !== 'held' && (
              <button className="with-icon" onClick={() => onPass(trip.id)}>
                <Icon name="ticket" size={16} />{t('trips.showPass')}
              </button>
            )}
            <button
              className="link" disabled={busy}
              onClick={async () => {
                setBusy(true)
                try { await ferry.cancelBooking(trip.id); onChanged() } finally { setBusy(false) }
              }}
            >
              {t('trips.cancel')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
